/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, MouseEvent } from "react";
import { Switch, FormControlLabel, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import css from "classnames";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import styles from "./tab-switch.module.css";

// calling preventDefault on the react synthetic event doesn't work in
// some edge cases, such as when this is used inside of a modal which is
// a child of a material-ui ClickAwayListener so calling preventDefault
// on the backing native event is a hacky workaround.

type Props = {
  id?: string;
  tabIsActive: boolean;
  switchIsOn: boolean;
  onSwitchClick: (event: MouseEvent) => void;
  onTabClick: (event: MouseEvent) => void;
  label: string;
  tabHasError?:boolean;
  tabErrorTooltip?: string;
}

const TabSwitch: React.FC<Props> = ({
  id,
  tabIsActive,
  switchIsOn,
  onSwitchClick,
  onTabClick,
  label,
  tabHasError,
  tabErrorTooltip,
}) => {
  const [myTabIsActive, setMyTabIsActive] = useState(false);
  const onChangeTabIsActive = () => setMyTabIsActive(tabIsActive);
  useEffect(onChangeTabIsActive, [tabIsActive]);
  const [mySwitchIsOn, setMySwitchIsOn] = useState(false);
  const onChangeSwitchIsOn = () => setMySwitchIsOn(switchIsOn);
  useEffect(onChangeSwitchIsOn, [switchIsOn]);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <div
      className={css(styles.tabSwitch, {
        [styles.tabSwitchActiveBorder]: myTabIsActive,
        [styles.tabSwitchActive]: myTabIsActive && !(mySwitchIsOn && tabHasError),
        [styles.tabSwitchActiveHasError]: myTabIsActive && mySwitchIsOn && tabHasError,
        [styles.tabSwitchHasError]: !myTabIsActive && mySwitchIsOn && tabHasError,
      })}
      onClick={(event: MouseEvent) => {
        event.preventDefault();
        event.nativeEvent.preventDefault();
        setMyTabIsActive(true);
        onTabClick(event);
      }}>
      <FormControlLabel
        control={
          <Switch
            id={id}
            size={isSmDown ? "small" : "medium"}
            onClick={(event: MouseEvent) => {
              event.preventDefault();
              event.nativeEvent.preventDefault();

              setMySwitchIsOn(!mySwitchIsOn);
              onSwitchClick(event);
            }}
            checked={mySwitchIsOn}/>
        }
        label={
          <span
            className={styles.tabLabel}
            onClick={(event: MouseEvent) => {
              // Prevent the label click from toggling the switch
              // Allow parent to handle tab selection
              event.preventDefault();
              event.nativeEvent.preventDefault();
            }}>
            {label}
            {mySwitchIsOn && tabHasError && (
              <Tooltip title={tabErrorTooltip || "Please resolve errors on this tab."} arrow placement="top">
                <WarningAmberIcon />
              </Tooltip>
            )}
          </span>
        }
      />
    </div>
  );
};

export default TabSwitch;
